import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { CaretDown, Check } from '@phosphor-icons/react';
import clsx from 'clsx';
import React, { useState } from 'react';
import type { PricingTierId } from './pricingTierOptions';

export interface PricingTierSelectOption {
  id: PricingTierId;
  primaryLabel: string;
  secondaryLabel?: string;
}

interface PricingTierSelectProps {
  ariaLabel: string;
  options: PricingTierSelectOption[];
  selectedId: PricingTierId;
  onSelect: (id: PricingTierId) => void;
  disabled?: boolean;
}

const PricingTierSelect: React.FC<PricingTierSelectProps> = ({
  ariaLabel,
  options,
  selectedId,
  onSelect,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const selectedOption = options.find((option) => option.id === selectedId) ?? options[0];

  if (!selectedOption) return null;

  return (
    <div onClick={(event) => event.stopPropagation()}>
      <DropdownMenu.Root open={disabled ? false : open} onOpenChange={disabled ? undefined : setOpen}>
        <DropdownMenu.Trigger asChild>
          <button
            type="button"
            disabled={disabled}
            aria-label={`${ariaLabel}: ${selectedOption.primaryLabel}`}
            className={clsx(
              'flex min-h-[42px] w-full items-center justify-between rounded-[10px] border px-3.5 py-2.5 text-left transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D8D5CF]/80 focus-visible:ring-offset-2',
              open
                ? 'border-[#E2DFD9] bg-[#F4F3F0]'
                : 'border-[#ECEAE6] bg-[#F8F7F5] hover:border-[#DFDCD6] hover:bg-[#F4F3F0]',
              disabled ? 'cursor-not-allowed opacity-55' : 'cursor-pointer',
            )}
          >
            <span className="min-w-0 truncate pr-3 text-[13px] font-medium leading-5 text-[#302E2A]">
              {selectedOption.primaryLabel}
            </span>
            <CaretDown
              size={15}
              weight="regular"
              className={clsx(
                'shrink-0 text-[#77736D] transition-transform duration-150',
                open && 'rotate-180',
              )}
            />
          </button>
        </DropdownMenu.Trigger>

        <DropdownMenu.Portal>
          <DropdownMenu.Content
            side="bottom"
            sideOffset={6}
            align="start"
            collisionPadding={16}
            className="z-[10002] overflow-hidden rounded-[10px] border border-[#E7E4DE] bg-white shadow-[0_10px_24px_rgba(33,31,27,0.10)] animate-in fade-in zoom-in-95 duration-100"
            style={{
              width: 'var(--radix-dropdown-menu-trigger-width)',
              maxWidth: 'calc(100vw - 32px)',
            }}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="max-h-[240px] overflow-y-auto p-1">
              {options.map((option) => {
                const isSelected = option.id === selectedId;
                return (
                  <DropdownMenu.Item
                    key={option.id}
                    onSelect={() => {
                      onSelect(option.id);
                      setOpen(false);
                    }}
                    className={clsx(
                      'flex cursor-pointer items-center justify-between gap-3 rounded-[7px] px-3 py-2 outline-none transition-colors hover:bg-[#F7F6F3] focus:bg-[#F7F6F3]',
                      isSelected ? 'text-[#1A162F]' : 'text-[#302E2A]',
                    )}
                  >
                    <div className="flex min-w-0 flex-1 flex-col items-start text-left">
                      <span className="min-w-0 truncate text-[13px] font-medium leading-5">
                        {option.primaryLabel}
                      </span>
                      {option.secondaryLabel && (
                        <span className={clsx(
                          "min-w-0 truncate text-[11.5px] font-semibold leading-4 mt-0.5",
                          isSelected ? "text-[#1A162F]/85" : "text-[#4F4C47]"
                        )}>
                          {option.secondaryLabel}
                        </span>
                      )}
                    </div>
                    {isSelected ? (
                      <Check size={14} weight="bold" className="shrink-0 text-[#302E2A]" />
                    ) : (
                      <span className="h-[14px] w-[14px] shrink-0" />
                    )}
                  </DropdownMenu.Item>
                );
              })}
            </div>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </div>
  );
};

export default PricingTierSelect;
